import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Loader2, Sparkles, Info, CheckCircle, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

const API = `${window.location.origin}/api`;
const MELANIN_ICON = "https://customer-assets.emergentagent.com/job_blackownedhub/artifacts/9kb68tzi_Melanin%20Icon%20-%20White%20with%20Black%20Background.png";

const BOOST_OPTIONS = [
  { id: 'small', amount: 1, label: '1 MEL' },
  { id: 'medium', amount: 5, label: '5 MEL' },
  { id: 'large', amount: 10, label: '10 MEL' },
];

export default function BoostBusinessButton({ businessId, businessName, melaninScore, onBoostComplete }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('medium');
  const [customAmount, setCustomAmount] = useState('');
  const [boosting, setBoosting] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);

  const openDialog = async () => {
    // Fetch wallet balance
    try {
      const response = await fetch(`${API}/wallet`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setWalletBalance(data.balance);
      } else {
        toast.error('Please sign in to boost this business');
        return;
      }
    } catch (error) {
      toast.error('Please sign in to boost this business');
      return;
    }
    setDialogOpen(true);
  };

  const getAmount = () => {
    if (selectedOption === 'custom') {
      return parseFloat(customAmount) || 0;
    }
    return BOOST_OPTIONS.find(o => o.id === selectedOption)?.amount || 0;
  };

  const handleBoost = async () => {
    const amount = getAmount();
    
    if (amount < 1) {
      toast.error('Minimum boost amount is 1 MEL');
      return;
    }
    
    if (amount > walletBalance) {
      toast.error('Insufficient MEL balance');
      return;
    }

    setBoosting(true);
    
    try {
      const response = await fetch(`${API}/businesses/${businessId}/boost`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          business_id: businessId,
          amount: amount
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to boost business');
      }

      toast.success(
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-green-400" />
          <span>{data.message}</span>
        </div>
      );
      
      setDialogOpen(false);
      onBoostComplete?.(data.new_melanin_score);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setBoosting(false);
    }
  };

  const amount = getAmount();
  const platformFee = amount * 0.10;
  const scoreContribution = amount - platformFee;
  const insufficientBalance = amount > (walletBalance || 0);

  return (
    <>
      <button
        onClick={openDialog}
        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 text-amber-400 hover:from-amber-500/30 hover:to-orange-500/30 transition-all group"
        data-testid="boost-business-btn"
      >
        <TrendingUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
        <span className="font-medium">Boost</span>
        {melaninScore > 0 && (
          <span className="text-xs bg-amber-500/20 px-2 py-0.5 rounded-full">
            {melaninScore.toFixed(1)}
          </span>
        )}
      </button>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#0F0F0F] border border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-amber-400" />
              Boost {businessName}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Increase this business&apos;s visibility in Community Top Picks
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            {/* Wallet Balance */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800">
              <span className="text-zinc-400 text-sm">Your Balance</span>
              <div className="flex items-center gap-2">
                <img src={MELANIN_ICON} alt="" className="w-4 h-4" />
                <span className={`font-bold ${walletBalance < 1 ? 'text-red-400' : 'text-white'}`}>
                  {walletBalance?.toFixed(2) || '0.00'}
                </span>
                <span className="text-zinc-500 text-sm">MEL</span>
              </div>
            </div>

            {/* Low Balance Warning & Buy MEL Link */}
            {walletBalance !== null && walletBalance < 5 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-amber-400" />
                  <span className="text-amber-400/90 text-sm">Low balance?</span>
                </div>
                <Link
                  to="/dashboard?tab=wallet"
                  onClick={() => setDialogOpen(false)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/20 text-amber-400 text-sm font-medium hover:bg-amber-500/30 transition-colors"
                  data-testid="buy-mel-link"
                >
                  <Sparkles className="w-3 h-3" />
                  Buy MEL
                </Link>
              </div>
            )}

            {/* Current Score */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <span className="text-amber-400/80 text-sm">Current Melanin Score</span>
              <span className="font-bold text-amber-400">{melaninScore?.toFixed(2) || '0.00'}</span>
            </div>

            {/* Boost Options */}
            <div className="space-y-3">
              <p className="text-zinc-400 text-sm">Select boost amount</p>
              <div className="grid grid-cols-4 gap-2">
                {BOOST_OPTIONS.map(option => (
                  <button
                    key={option.id}
                    onClick={() => {
                      setSelectedOption(option.id);
                      setCustomAmount('');
                    }}
                    className={`p-3 rounded-lg border text-center transition-all ${
                      selectedOption === option.id
                        ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                        : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                    }`}
                    data-testid={`boost-option-${option.id}`}
                  >
                    <div className="font-bold text-lg">{option.amount}</div>
                    <div className="text-xs opacity-70">MEL</div>
                  </button>
                ))}
                <button
                  onClick={() => setSelectedOption('custom')}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    selectedOption === 'custom'
                      ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                      : 'bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                  }`}
                  data-testid="boost-option-custom"
                >
                  <div className="font-bold text-sm">Custom</div>
                  <div className="text-xs opacity-70">amount</div>
                </button>
              </div>

              {/* Custom Amount Input */}
              <AnimatePresence>
                {selectedOption === 'custom' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="relative mt-2">
                      <img src={MELANIN_ICON} alt="" className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" />
                      <Input
                        type="number"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        placeholder="Enter amount (min 1)"
                        min={1}
                        step={0.01}
                        className="bg-zinc-900 border-zinc-800 text-white pl-10"
                        data-testid="boost-custom-amount"
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Fee Breakdown */}
            {amount > 0 && (
              <div className="space-y-2 p-3 rounded-lg bg-zinc-900/30 border border-zinc-800">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">You pay</span>
                  <span className="text-white">{amount.toFixed(2)} MEL</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500">Platform fee (10%)</span>
                  <span className="text-zinc-400">-{platformFee.toFixed(2)} MEL</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-zinc-800">
                  <span className="text-amber-400">Added to Melanin Score</span>
                  <span className="text-amber-400 font-bold">+{scoreContribution.toFixed(2)}</span>
                </div>
              </div>
            )}

            {/* Insufficient Balance Warning */}
            {insufficientBalance && amount > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <span className="text-red-400 text-sm">Insufficient balance</span>
                <Link
                  to="/dashboard?tab=wallet"
                  onClick={() => setDialogOpen(false)}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white text-black text-sm font-medium hover:bg-zinc-200 transition-colors"
                  data-testid="buy-mel-insufficient"
                >
                  <CreditCard className="w-3 h-3" />
                  Buy MEL Now
                </Link>
              </div>
            )}

            {/* Info */}
            <div className="flex items-start gap-2 text-xs text-zinc-500">
              <Info className="w-4 h-4 shrink-0 mt-0.5" />
              <p>
                Higher Melanin Scores help businesses appear in &quot;Community Top Picks&quot; 
                and rank higher in search results. 10% platform fee applies.
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleBoost}
              disabled={amount < 1 || insufficientBalance || boosting}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold hover:from-amber-500 hover:to-orange-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="confirm-boost-btn"
            >
              {boosting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Boosting...
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5" />
                  Boost for {amount > 0 ? amount.toFixed(2) : '0'} MEL
                </>
              )}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
